import { useEffect, useRef, useState } from 'react'

import dayGridPlugin from '@fullcalendar/daygrid' // a plugin!
import FullCalendar from '@fullcalendar/react' // must go before plugins
import { Tooltip } from '@mui/material'
import Box from '@mui/material/Box'
import { useQuery } from '@tanstack/react-query'
import LoadingFullPage from 'components/LoadingFullPage'
import { QUERY_KEYS } from 'constants/keys'
import { ACTIVITY_SITATUS_MAP_COLOR } from 'constants/types'
import moment from 'moment'
import { ApiService } from 'services/api'

function getRandomColor() {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

type Event = {
  id: string
  start: string
  end: string
  title: string
}

export default function ActivitiesCalendar() {
  const [start, setStart] = useState<string>('')
  const [end, setEnd] = useState<string>('')

  const { data: activities, isLoading } = useQuery<Activity[]>({
    queryKey: [QUERY_KEYS.ACTIVITIES.LIST_CALENDAR, start, end],
    queryFn: async ({ queryKey }) => {
      const [, start, end] = queryKey

      if (!start) {
        return []
      }

      return await ApiService.Activities.getAllCalendarByPeriod(
        String(start),
        String(end),
      )
    },
  })

  const [events, setEvents] = useState<Event[]>([])

  const calendarRef = useRef<HTMLDivElement>()

  if (calendarRef.current) {
    calendarRef.current.children[0].setAttribute('style', 'min-height: 100vh;')
  }

  useEffect(() => {
    if (events.length) {
      const colors: { [key: string]: string } = {}
      events.forEach((event) => {
        colors[event.id] = getRandomColor()
      })
    }
  }, [events])

  useEffect(() => {
    if (activities?.length) {
      const events = activities.map((activity) => {
        const guides = activity.responsibleGuidesActivities
          ?.filter(
            (guide) =>
              guide.collaborator.name.toLowerCase() !== 'app_usuario' &&
              guide.collaborator.name.toLowerCase() !== 'alessandro martins',
          )
          ?.map((guide) => {
            const guideNames = guide.collaborator.name.split(' ')
            return `${guideNames[0]} ${guideNames[guideNames.length - 1]}`
          })
          .join(', ')

        return {
          id: String(activity.id),
          title: `${activity.institution?.fantasy_name} - ${activity.name}`,
          start: moment(activity.start_travel_date, 'DD/MM/YYYY').format(
            'YYYY-MM-DD',
          ),
          end: moment(activity.end_travel_date, 'DD/MM/YYYY')
            .add(1, 'd')
            .format('YYYY-MM-DD'),
          backgroundColor: ACTIVITY_SITATUS_MAP_COLOR[activity.status],
          extendedProps: {
            code: activity.code,
            guides,
            shipping_company: activity.shipping_company,
            type_shipping_company: activity.type_shipping_company,
            enrollment_limit: activity.enrollment_limit,
          },
        }
      })
      setEvents(events)
    }
  }, [activities])

  const week = document.getElementsByClassName('fc-dayGridWeek-button')[0]
  const month = document.getElementsByClassName('fc-dayGridMonth-button')[0]

  if (week) {
    week.setAttribute(
      'style',
      'width: 85px;overflow: hidden;white-space: nowrap; word-spacing: 20px',
    )
    week.textContent = 'Semana '
  }
  if (month) {
    month.setAttribute(
      'style',
      'width: 55px;overflow: hidden;white-space: nowrap; word-spacing: 20px',
    )
    month.innerHTML = 'Mês '
  }

  return (
    <>
      {isLoading && <LoadingFullPage />}
      <Box component="main" py={{ md: 8 }} px={{ md: 8 }} ref={calendarRef}>
        <FullCalendar
          datesSet={(newDate) => {
            const start = moment(newDate.view.activeStart, 'YYYY-MM-DD')
              .utc()
              .format('YYYY-MM-DD')
            const end = moment(newDate.view.activeEnd, 'YYYY-MM-DD').format(
              'YYYY-MM-DD',
            )

            setStart(start)
            setEnd(end)
          }}
          plugins={[dayGridPlugin]}
          locale="pt"
          initialView="dayGridMonth"
          timeZone="-03:00"
          events={events}
          eventContent={(info) => (
            <div className="fc-event-main-frame">
              <div className="fc-event-title-container">
                <Tooltip
                  placement="bottom"
                  title={
                    <div>
                      <p>
                        <b>Código: {info.event.extendedProps.code}</b>
                      </p>
                      <p>Guias: {info.event.extendedProps.guides}</p>
                      <p>
                        Tipo transporte:{' '}
                        {info.event.extendedProps.type_shipping_company}
                      </p>
                      <p>
                        Transportadora:{' '}
                        {info.event.extendedProps.shipping_company}
                      </p>
                      <p>
                        Limite inscrição:{' '}
                        {info.event.extendedProps.enrollment_limit}
                      </p>
                    </div>
                  }
                >
                  <a
                    href={`/commercial/activities/view/${info.event.id}`}
                    style={{
                      textDecoration: 'none',
                      whiteSpace: 'normal',
                      margin: '5px 0px',
                      color: '#fff',
                    }}
                    className="fc-event-title fc-sticky"
                  >
                    {info.event.title}
                  </a>
                </Tooltip>
              </div>
            </div>
          )}
          headerToolbar={{
            left: 'prev,next',
            center: 'title',
            right: 'dayGridWeek,dayGridMonth',
          }}
        />
      </Box>
    </>
  )
}
